import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { OrderDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';
import { apiFetch, ApiError } from '../api/client.js';
import { useCartStore, type CartLine } from '../store/cart.js';
import { labels } from '../labels.js';
import { BottomNav } from '../components/BottomNav.js';
import { ClientHeader } from '../components/ClientHeader.js';
import { ConfirmSheet } from '../components/ConfirmSheet.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { QuantityStepper } from '../components/QuantityStepper.js';
import styles from './CartPage.module.css';

interface RemovedLine {
  line: CartLine;
  index: number;
}

export function CartPage() {
  const navigate = useNavigate();
  const cart = useCartStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailableIds, setUnavailableIds] = useState<Set<string>>(new Set());
  const [removed, setRemoved] = useState<RemovedLine | null>(null);
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [confirmClear, setConfirmClear] = useState(false);
  const undoTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
  }, []);

  const removeWithUndo = (line: CartLine, index: number) => {
    cart.removeLine(line.menuItemId);
    setRemoved({ line, index });
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
    undoTimer.current = window.setTimeout(() => setRemoved(null), 5000);
  };

  const undoRemove = () => {
    if (!removed) return;
    cart.restoreLine(removed.line, removed.index);
    setRemoved(null);
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
  };

  const undoToast = removed && (
    <div role="status" className={`toast ${styles.toast}`}>
      <span>{labels.cart.removed} {removed.line.nameSnapshot}</span>
      <button type="button" onClick={undoRemove}>{labels.cart.undo}</button>
    </div>
  );

  if (cart.lines.length === 0) {
    return (
      <div className={`client-screen ${styles.screen}`}>
        <ClientHeader title={labels.cart.title} backTo="/menu" backLabel={labels.cart.back} />
        <main className={styles.emptyMain}>
          <EmptyState icon="bag" title={labels.cart.emptyTitle} text={labels.cart.empty}>
            <Link to="/menu" className="btn-primary btn-lg">
              <Icon name="menu" size={20} />
              {labels.cart.browse}
            </Link>
          </EmptyState>
        </main>
        {undoToast}
        <BottomNav />
      </div>
    );
  }

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setUnavailableIds(new Set());
    try {
      const order = await apiFetch<OrderDTO>('/public/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.lines.map((l) => ({ menuItemId: l.menuItemId, quantity: l.quantity, note: l.note })),
          customerNote: cart.customerNote || undefined,
        }),
      });
      cart.clear();
      navigate(`/commandes/${order.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ORDER_ITEM_UNAVAILABLE') {
        setError(labels.errors.itemsUnavailable);
        const ids = (e.details as { items?: string[] } | null)?.items;
        if (Array.isArray(ids)) setUnavailableIds(new Set(ids));
      } else if (e instanceof ApiError) {
        setError(`${e.message} (${e.code})`);
      } else {
        setError(labels.errors.generic);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const currency = cart.lines.find((l) => l.currency)?.currency ?? 'EUR';
  const toggleNote = (id: string) =>
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div className={`client-screen ${styles.screen}`}>
      <ClientHeader
        title={labels.cart.title}
        subtitle={labels.menu.itemCount(cart.itemCount())}
        backTo="/menu"
        backLabel={labels.cart.back}
      />

      <main className={styles.main}>
        {error && (
          <p role="alert" className="notice notice--danger">
            <Icon name="alert" size={18} />
            {error}
          </p>
        )}

        <ul className={styles.lines}>
          {cart.lines.map((line, index) => {
            const unavailable = unavailableIds.has(line.menuItemId);
            const noteOpen = openNotes.has(line.menuItemId) || Boolean(line.note);
            return (
              <li key={line.menuItemId} className={`${styles.line} ${unavailable ? styles.lineError : ''}`}>
                <div className={styles.lineMain}>
                  <div className="grow">
                    <p className={styles.lineName}>{line.nameSnapshot}</p>
                    <p className={styles.linePrice}>
                      {formatMoney(line.unitPriceMinor * line.quantity, line.currency ?? currency)}
                      {line.quantity > 1 && (
                        <span> · {labels.cart.unitPrice(formatMoney(line.unitPriceMinor, line.currency ?? currency))}</span>
                      )}
                    </p>
                    {unavailable && <span className="badge badge--cancelled">{labels.menu.unavailable}</span>}
                  </div>
                  <QuantityStepper
                    name={line.nameSnapshot}
                    quantity={line.quantity}
                    onChange={(q) => {
                      if (q <= 0) removeWithUndo(line, index);
                      else cart.setQuantity(line.menuItemId, q);
                    }}
                  />
                </div>
                {noteOpen ? (
                  <input
                    type="text"
                    value={line.note ?? ''}
                    onChange={(e) => cart.setLineNote(line.menuItemId, e.target.value)}
                    placeholder={labels.cart.itemNotePlaceholder}
                    aria-label={`${labels.cart.itemNoteLabel} - ${line.nameSnapshot}`}
                    className={`input ${styles.lineNote}`}
                    autoFocus={openNotes.has(line.menuItemId) && !line.note}
                  />
                ) : (
                  <button type="button" className={`btn-link ${styles.addNote}`} onClick={() => toggleNote(line.menuItemId)}>
                    <Icon name="edit" size={16} />
                    {labels.cart.addItemNote}
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <section className={styles.block}>
          <label htmlFor="customer-note" className={styles.blockTitle}>{labels.cart.noteLabel}</label>
          <textarea
            id="customer-note"
            value={cart.customerNote}
            onChange={(e) => cart.setCustomerNote(e.target.value)}
            placeholder={labels.cart.notePlaceholder}
            className="input"
          />
        </section>

        <button
          type="button"
          onClick={() => setConfirmClear(true)}
          disabled={submitting}
          className={`btn-link btn-link--danger ${styles.clear}`}
        >
          <Icon name="trash" size={16} />
          {labels.cart.clearCart}
        </button>
      </main>

      <footer className={styles.checkout}>
        <div className={styles.checkoutInner}>
          <div className={styles.total}>
            <span>{labels.cart.total}</span>
            <strong>{formatMoney(cart.totalMinor(), currency)}</strong>
          </div>
          <button type="button" onClick={() => void submit()} disabled={submitting} className="btn-primary btn-lg grow">
            {submitting ? labels.cart.submitting : labels.cart.submit}
          </button>
        </div>
      </footer>

      {undoToast}

      {confirmClear && (
        <ConfirmSheet
          title={labels.cart.clearCart}
          message={labels.cart.clearCartConfirm}
          confirmLabel={labels.cart.clearCart}
          onConfirm={() => { setConfirmClear(false); cart.clear(); }}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </div>
  );
}
